import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  MusicalNoteIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { Slider } from '@/components/ui/slider';

interface SoundscapeConfig {
  id: string;
  title: string;
  category: string;
  icon: string;
}

const SOUNDSCAPES: SoundscapeConfig[] = [
  // Nature
  { id: 'rain', title: 'Rain', category: 'Nature', icon: '🌧️' },
  { id: 'ocean', title: 'Ocean Waves', category: 'Nature', icon: '🌊' },
  { id: 'forest', title: 'Forest', category: 'Nature', icon: '🌲' },
  { id: 'wind', title: 'Wind', category: 'Nature', icon: '💨' },
  // Noise
  { id: 'white-noise', title: 'White Noise', category: 'Noise', icon: '📻' },
  { id: 'brown-noise', title: 'Brown Noise', category: 'Noise', icon: '🟤' },
  { id: 'pink-noise', title: 'Pink Noise', category: 'Noise', icon: '🩷' },
  // Ambient
  { id: 'cafe', title: 'Coffee Shop', category: 'Ambient', icon: '☕' },
  { id: 'fireplace', title: 'Fireplace', category: 'Ambient', icon: '🔥' },
  { id: 'deep-space', title: 'Deep Space', category: 'Ambient', icon: '🌌' },
];

const CATEGORIES = ['All', 'Nature', 'Noise', 'Ambient'] as const;

// ---- Web Audio API Sound Generators ----

type StopFn = () => void;

function createNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const bufferSize = sampleRate * seconds;
  const buffer = ctx.createBuffer(1, bufferSize, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

function startSoundscape(ctx: AudioContext, id: string, gainNode: GainNode): StopFn {
  const nodes: AudioNode[] = [];

  const connectAndTrack = (node: AudioNode) => {
    nodes.push(node);
    return node;
  };

  const noiseBuffer = createNoiseBuffer(ctx, 4);

  const createLoopingNoise = (filterType: BiquadFilterType, frequency: number, q = 1): AudioBufferSourceNode => {
    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer;
    source.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = q;
    source.connect(filter);
    filter.connect(gainNode);
    connectAndTrack(source);
    connectAndTrack(filter);
    source.start();
    return source;
  };

  switch (id) {
    case 'white-noise': {
      const source = ctx.createBufferSource();
      source.buffer = noiseBuffer;
      source.loop = true;
      source.connect(gainNode);
      connectAndTrack(source);
      source.start();
      break;
    }

    case 'brown-noise': {
      createLoopingNoise('lowpass', 400, 0.5);
      break;
    }

    case 'pink-noise': {
      createLoopingNoise('lowpass', 1600, 0.7);
      // Add a gentle high shelf to soften highs
      const source2 = ctx.createBufferSource();
      source2.buffer = noiseBuffer;
      source2.loop = true;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highshelf';
      hp.frequency.value = 3000;
      hp.gain.value = -12;
      source2.connect(hp);
      hp.connect(gainNode);
      connectAndTrack(source2);
      connectAndTrack(hp);
      source2.start();
      break;
    }

    case 'rain': {
      // Base rain: filtered noise
      createLoopingNoise('bandpass', 3000, 0.3);
      // Additional higher frequency layer for rain detail
      createLoopingNoise('highpass', 5000, 0.5);
      // Low rumble for distant thunder feel
      const rumble = ctx.createBufferSource();
      rumble.buffer = noiseBuffer;
      rumble.loop = true;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 200;
      const rumbleGain = ctx.createGain();
      rumbleGain.gain.value = 0.3;
      rumble.connect(lp);
      lp.connect(rumbleGain);
      rumbleGain.connect(gainNode);
      connectAndTrack(rumble);
      connectAndTrack(lp);
      connectAndTrack(rumbleGain);
      rumble.start();
      break;
    }

    case 'ocean': {
      // Base ocean: low-passed noise
      const oceanSource = ctx.createBufferSource();
      oceanSource.buffer = noiseBuffer;
      oceanSource.loop = true;
      const oceanFilter = ctx.createBiquadFilter();
      oceanFilter.type = 'lowpass';
      oceanFilter.frequency.value = 600;
      // LFO for wave-like modulation
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.08; // Very slow oscillation
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 400;
      lfo.connect(lfoGain);
      lfoGain.connect(oceanFilter.frequency);
      oceanSource.connect(oceanFilter);
      oceanFilter.connect(gainNode);
      connectAndTrack(oceanSource);
      connectAndTrack(oceanFilter);
      connectAndTrack(lfo);
      connectAndTrack(lfoGain);
      oceanSource.start();
      lfo.start();
      break;
    }

    case 'forest': {
      // Base: gentle wind through trees
      createLoopingNoise('bandpass', 1000, 0.2);
      // Higher frequency rustling
      createLoopingNoise('bandpass', 4000, 0.4);
      break;
    }

    case 'wind': {
      const windSource = ctx.createBufferSource();
      windSource.buffer = noiseBuffer;
      windSource.loop = true;
      const windFilter = ctx.createBiquadFilter();
      windFilter.type = 'bandpass';
      windFilter.frequency.value = 800;
      windFilter.Q.value = 0.5;
      // Slow LFO for wind gusts
      const windLfo = ctx.createOscillator();
      windLfo.frequency.value = 0.15;
      const windLfoGain = ctx.createGain();
      windLfoGain.gain.value = 500;
      windLfo.connect(windLfoGain);
      windLfoGain.connect(windFilter.frequency);
      windSource.connect(windFilter);
      windFilter.connect(gainNode);
      connectAndTrack(windSource);
      connectAndTrack(windFilter);
      connectAndTrack(windLfo);
      connectAndTrack(windLfoGain);
      windSource.start();
      windLfo.start();
      break;
    }

    case 'cafe': {
      // Brown noise base (murmur)
      createLoopingNoise('lowpass', 600, 0.3);
      // Mid-range chatter texture
      createLoopingNoise('bandpass', 1500, 0.4);
      break;
    }

    case 'fireplace': {
      // Crackling: filtered noise with modulation
      const fireSource = ctx.createBufferSource();
      fireSource.buffer = noiseBuffer;
      fireSource.loop = true;
      const fireLp = ctx.createBiquadFilter();
      fireLp.type = 'lowpass';
      fireLp.frequency.value = 1200;
      const fireHp = ctx.createBiquadFilter();
      fireHp.type = 'highpass';
      fireHp.frequency.value = 200;
      fireSource.connect(fireLp);
      fireLp.connect(fireHp);
      fireHp.connect(gainNode);
      connectAndTrack(fireSource);
      connectAndTrack(fireLp);
      connectAndTrack(fireHp);
      fireSource.start();
      // Low warmth
      createLoopingNoise('lowpass', 300, 0.2);
      break;
    }

    case 'deep-space': {
      // Deep drone
      const drone = ctx.createOscillator();
      drone.type = 'sine';
      drone.frequency.value = 60;
      const droneGain = ctx.createGain();
      droneGain.gain.value = 0.4;
      drone.connect(droneGain);
      droneGain.connect(gainNode);
      connectAndTrack(drone);
      connectAndTrack(droneGain);
      drone.start();
      // Subtle higher harmonic
      const harm = ctx.createOscillator();
      harm.type = 'sine';
      harm.frequency.value = 90;
      const harmGain = ctx.createGain();
      harmGain.gain.value = 0.15;
      harm.connect(harmGain);
      harmGain.connect(gainNode);
      connectAndTrack(harm);
      connectAndTrack(harmGain);
      harm.start();
      // Very filtered noise layer
      createLoopingNoise('lowpass', 200, 0.1);
      break;
    }
  }

  return () => {
    nodes.forEach(node => {
      try {
        node.disconnect();
        if ('stop' in node && typeof (node as any).stop === 'function') {
          (node as any).stop();
        }
      } catch {}
    });
  };
}

// ---- Component ----

export const MusicPlayer: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSoundId, setCurrentSoundId] = useState('rain');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const stopFnRef = useRef<StopFn | null>(null);

  const currentSound = SOUNDSCAPES.find(s => s.id === currentSoundId) || SOUNDSCAPES[0];

  const filteredSounds = selectedCategory === 'All'
    ? SOUNDSCAPES
    : SOUNDSCAPES.filter(s => s.category === selectedCategory);

  const getOrCreateCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new AudioContext();
      gainNodeRef.current = audioCtxRef.current.createGain();
      gainNodeRef.current.gain.value = isMuted ? 0 : volume;
      gainNodeRef.current.connect(audioCtxRef.current.destination);
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return { ctx: audioCtxRef.current, gain: gainNodeRef.current! };
  }, []);

  const stopCurrent = useCallback(() => {
    if (stopFnRef.current) {
      stopFnRef.current();
      stopFnRef.current = null;
    }
  }, []);

  const playSoundscape = useCallback((soundId: string) => {
    stopCurrent();
    const { ctx, gain } = getOrCreateCtx();
    gain.gain.value = isMuted ? 0 : volume;
    stopFnRef.current = startSoundscape(ctx, soundId, gain);
  }, [getOrCreateCtx, stopCurrent, volume, isMuted]);

  // Play/stop when isPlaying or sound changes
  useEffect(() => {
    if (isPlaying) {
      playSoundscape(currentSoundId);
    } else {
      stopCurrent();
    }
    return () => stopCurrent();
  }, [isPlaying, currentSoundId]);

  // Update volume in real time
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCurrent();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close();
      }
    };
  }, []);

  const handlePlayPause = () => setIsPlaying(prev => !prev);

  const handleSelectSound = (id: string) => {
    setCurrentSoundId(id);
    if (!isPlaying) setIsPlaying(true);
  };

  const handleVolumeChange = (values: number[]) => {
    const v = values[0] / 100;
    setVolume(v);
    setIsMuted(v === 0);
  };

  return (
    <div className="bg-card/60 backdrop-blur-sm rounded-2xl border border-border/50 shadow-sm overflow-hidden">
      {/* Collapsed header */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsExpanded(!isExpanded); } }}
        className="flex items-center justify-between p-4 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "p-2 rounded-xl",
            isPlaying ? "bg-purple-500/10" : "bg-muted"
          )}>
            <MusicalNoteIcon className={cn(
              "w-5 h-5",
              isPlaying ? "text-purple-500" : "text-muted-foreground"
            )} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">
              {isPlaying ? `${currentSound.icon} ${currentSound.title}` : 'Ambient Sounds'}
            </h3>
            <p className="text-xs text-muted-foreground">
              {isPlaying ? `${currentSound.category} · Playing` : 'Focus soundscapes'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}
            className={cn(
              "p-2 rounded-full transition-colors",
              isPlaying ? "bg-purple-500/10 text-purple-500 hover:bg-purple-500/20" : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {isPlaying ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
          </button>
          {isExpanded ? <ChevronUpIcon className="w-4 h-4 text-muted-foreground" /> : <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Categories */}
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors",
                  selectedCategory === cat
                    ? "bg-purple-500 text-white"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Sound grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {filteredSounds.map(sound => (
              <button
                key={sound.id}
                onClick={() => handleSelectSound(sound.id)}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all",
                  currentSoundId === sound.id && isPlaying
                    ? "bg-purple-500/10 border border-purple-500/30 text-purple-600 dark:text-purple-400"
                    : "bg-muted/40 border border-transparent hover:bg-muted/60 text-foreground"
                )}
              >
                <span className="text-lg">{sound.icon}</span>
                <span className="text-sm font-medium truncate">{sound.title}</span>
              </button>
            ))}
          </div>

          {/* Volume */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {isMuted || volume === 0 ? <SpeakerXMarkIcon className="w-4 h-4" /> : <SpeakerWaveIcon className="w-4 h-4" />}
            </button>
            <Slider
              value={[isMuted ? 0 : volume * 100]}
              onValueChange={handleVolumeChange}
              max={100}
              step={1}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-8 text-right">{isMuted ? 0 : Math.round(volume * 100)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};
